import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SiteMobileHeader from '../components/SiteMobileHeader';
import StickyNavigation from '../components/StickyNavigation';
import HomeContactFooter from '../components/HomeContactFooter';
import MailingListModal from '../components/MailingListModal';
import ProjectLocationMap from '../components/ProjectLocationMap';
import homeHeroLogo from '../assets/Group12.svg';
import { shot1Image, eiraMobileShowcaseImage as eiraHeroMobileImage } from '../cdn/landingMedia';
import eiraCardLogo from '../assets/project/eiraLogo.svg';
import cardScrollIcon from '../assets/project/SVG.svg';
import meetsIllustration from '../assets/project/meets.svg';
import {
  eiraBrochurePdf,
  visualBedroom,
  visualBuildingFacade,
  visualClubSerene,
  visualLivingRoom,
  visualSerenityWalkway,
} from '../cdn/eiraMedia';
import amenitySerenityIcon from '../assets/project/eira-amenities/serenity.svg';
import amenityGymIcon from '../assets/project/eira-amenities/gym.svg';
import amenityYogaIcon from '../assets/project/eira-amenities/yoga.svg';
import amenityPoolBarIcon from '../assets/project/eira-amenities/pool-bar.svg';
import amenityKidsPlayIcon from '../assets/project/eira-amenities/kids-play.svg';
import amenityPetIcon from '../assets/project/eira-amenities/pet-park.svg';
import { clearContactState, submitMailingListForm } from '../store/slices/contactSlice';
import { validateMailingListForm } from '../utils/mailingListValidation';
import { showToast } from '../utils/toast';
import { downloadAsset } from '../utils/downloadAsset';
import useReplayOnScrollReveal from '../hooks/useReplayOnScrollReveal';
import useStaggeredRiseOnView, { staggerLineClass } from '../hooks/useStaggeredRiseOnView';
import './LandingPage.css';
import './EiraPage.css';
import '../styles/SitePageShell.css';

const MAIL_MODAL_CLOSE_MS = 620;
const EIRA_MAP_EMBED_URL =
  'https://maps.google.com/maps?q=25.034937499999998,55.1963125&hl=en&z=15&output=embed';
const EIRA_MAP_CENTER = { lat: 25.0349375, lng: 55.1963125 };

const EIRA_AMENITIES = [
  { icon: amenitySerenityIcon, label: 'Serenity Walkway' },
  { icon: amenityGymIcon, label: 'Gymnasium' },
  { icon: amenityYogaIcon, label: 'Arial Yoga' },
  { icon: amenityPoolBarIcon, label: 'Pool Bar with BBQ Area' },
  { icon: amenityKidsPlayIcon, label: 'Kids Play Area' },
  { icon: amenityPetIcon, label: 'Pet Park' },
];

const EIRA_MAP_TABS = ['Schools', 'Shop', 'Dine', 'Grocery', 'Attractions'];

const EIRA_VISUAL_SLIDES = [
  visualSerenityWalkway,
  visualBedroom,
  visualLivingRoom,
  visualBuildingFacade,
  visualClubSerene,
];
const EIRA_VISUAL_SLIDES_MOBILE = EIRA_VISUAL_SLIDES;
const EIRA_VISUAL_AUTOPLAY_MS = 4500;

const EIRA_OVERVIEW_SECOND_PARA_INDEX = 5;

const EIRA_OVERVIEW = {
  titleLines: ['Where tranquility', 'meets modern living'],
  mobileParagraphs: [
    'Nestled in the heart of Dubai Production City, EIRA is a thoughtfully crafted residential address where contemporary architecture, lush green surroundings, and everyday convenience come together in perfect harmony.',
    'Offering an exclusive collection of Studios and 1-Bedroom residences, every home is intelligently designed with efficient layouts, elegant finishes, and expansive windows that invite natural light while framing serene park and lake views. Every detail has been curated to create a lifestyle that feels calm, connected, and effortlessly refined.',
  ],
  desktopBodyLines: [
    'Nestled in the heart of Dubai Production City, EIRA',
    'is a thoughtfully crafted residential address',
    'where contemporary architecture, lush green',
    'surroundings, and everyday convenience come',
    'together in perfect harmony.',
    'Offering an exclusive collection of Studios and 1-',
    'Bedroom residences, every home is intelligently',
    'designed with efficient layouts, elegant finishes,',
    'and expansive windows that invite natural light',
    'while framing serene park and lake views. Every',
    'detail has been curated to create a lifestyle that',
    'feels calm, connected, and effortlessly refined.',
  ],
};

const EIRA_OVERVIEW_BODY_LINE_CLASS =
  "block font-['Poppins',sans-serif] text-[16px] font-light leading-[26px] text-[#0A0A0AC7] lg:text-[19.7px] lg:leading-[30px]";

const EIRA_OVERVIEW_CTA_CLASS =
  "inline-flex h-[43px] w-[240px] min-w-[240px] max-w-full items-center justify-center whitespace-nowrap rounded-[25px] border border-solid border-black px-[27.7px] font-['Inter',sans-serif] text-[12px] font-normal uppercase leading-[18px] tracking-[2.5px] text-black transition-colors duration-200 hover:bg-black hover:text-white";

const getVisualDposClass = (offset) => {
  if (offset === 0) return 'eira-visual-dpos--center';
  if (offset === -1) return 'eira-visual-dpos--left';
  if (offset === 1) return 'eira-visual-dpos--right';
  if (offset === -2) return 'eira-visual-dpos--far-left';
  if (offset === 2) return 'eira-visual-dpos--far-right';
  return offset < 0 ? 'eira-visual-dpos--hidden-left' : 'eira-visual-dpos--hidden-right';
};
const EIRA_VISUAL_COUNT = EIRA_VISUAL_SLIDES.length;
const EIRA_VISUAL_PAIRS = EIRA_VISUAL_SLIDES.map((d, i) => ({ d, m: EIRA_VISUAL_SLIDES_MOBILE[i] }));
// Clone last slide before + first two slides after for seamless infinite loop.
const EIRA_VISUAL_EXTENDED = [
  EIRA_VISUAL_PAIRS[EIRA_VISUAL_COUNT - 1],
  ...EIRA_VISUAL_PAIRS,
  EIRA_VISUAL_PAIRS[0],
  EIRA_VISUAL_PAIRS[1],
];

const EiraPage = () => {
  const dispatch = useDispatch();
  const contactSubmitting = useSelector((state) => state.contact.loading);

  const [mailOpen, setMailOpen] = useState(false);
  const [mailClosing, setMailClosing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [promotionalConsent, setPromotionalConsent] = useState(false);
  const [activeMapTab, setActiveMapTab] = useState('Schools');
  // Track position runs over the extended (cloned) list; index 1 = first real slide.
  const [visualPos, setVisualPos] = useState(1);
  const [visualAnimate, setVisualAnimate] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 402));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useReplayOnScrollReveal('.project-reveal-on-scroll', 'is-visible', 0.12, '80px 0px -10% 0px', false);

  const overviewRevealRef = useRef(null);

  useStaggeredRiseOnView(overviewRevealRef, {
    individual: true,
    durationMs: 1200,
    distancePx: 28,
    threshold: 0.14,
    rootMargin: '0px 0px -8% 0px',
    replay: true,
    reverseOnLeave: true,
    desktopOnly: false,
  });

  const visualPosRef = useRef(visualPos);

  useEffect(() => {
    visualPosRef.current = visualPos;
  }, [visualPos]);

  const advanceVisualSlide = useCallback((direction) => {
    setVisualAnimate(true);
    setVisualPos((p) => Math.max(0, Math.min(EIRA_VISUAL_COUNT + 1, p + direction)));
  }, []);

  const finishVisualSlide = useCallback((pos) => {
    if (pos >= EIRA_VISUAL_COUNT + 1) {
      setVisualAnimate(false);
      setVisualPos(1);
    } else if (pos <= 0) {
      setVisualAnimate(false);
      setVisualPos(EIRA_VISUAL_COUNT);
    }
  }, []);

  const goVisualSlide = useCallback((direction) => {
    advanceVisualSlide(direction);
  }, [advanceVisualSlide]);

  const handleDeskTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'left') return;
    finishVisualSlide(visualPosRef.current);
  }, [finishVisualSlide]);

  const handleMobileTransitionEnd = useCallback((e) => {
    if (e?.propertyName !== 'transform') return;
    finishVisualSlide(visualPosRef.current);
  }, [finishVisualSlide]);

  useEffect(() => {
    document.title = 'Eira | Neoterra Developments';
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => advanceVisualSlide(1), EIRA_VISUAL_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [advanceVisualSlide]);

  // Re-enable the transition on the next frame after a no-transition loop reset.
  useEffect(() => {
    if (visualAnimate) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setVisualAnimate(true)));
    return () => cancelAnimationFrame(raf);
  }, [visualAnimate]);

  const handleDownloadBrochure = useCallback(() => {
    downloadAsset(eiraBrochurePdf, 'Eira Brochure.pdf');
  }, []);

  const openMailModal = useCallback(() => {
    setMailClosing(false);
    setMailOpen(true);
  }, []);

  const closeMailModal = useCallback(() => {
    if (mailClosing || !mailOpen) return;
    setMailClosing(true);
    window.setTimeout(() => {
      setMailOpen(false);
      setMailClosing(false);
    }, MAIL_MODAL_CLOSE_MS);
  }, [mailClosing, mailOpen]);

  const handleMailSubmit = async (e) => {
    e.preventDefault();
    const validation = validateMailingListForm({ fullName, email, phone });

    if (!validation.isValid) {
      showToast(validation.error, 'error');
      return;
    }

    const { name, email: emailValue, contactNumber } = validation.values;

    try {
      await dispatch(
        submitMailingListForm({
          name,
          email: emailValue,
          contactNumber,
          promotionalConsent,
          sourcePage: 'eira',
        }),
      ).unwrap();
      showToast('Thanks! We will keep you informed.', 'success');
      setFullName('');
      setEmail('');
      setPhone('');
      setPromotionalConsent(false);
      closeMailModal();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Something went wrong. Please try again.', 'error');
    } finally {
      dispatch(clearContactState());
    }
  };

  return (
    <div className="landing-page eira-page site-page-shell">
      <SiteMobileHeader
        variant={viewportWidth >= 1024 ? 'homeHero' : undefined}
        logoSrc={viewportWidth >= 1024 ? homeHeroLogo : undefined}
        barClassName="home-top-bar"
        onEnquireClick={openMailModal}
      />

      <div className="relative w-full">
        <section className="eira-page-hero relative h-[100vh] min-h-[600px] w-full overflow-hidden" aria-label="Eira hero">
          <picture className="eira-page-hero__image absolute inset-0 z-[1] block h-full w-full">
            <source media="(min-width: 1024px)" srcSet={shot1Image} />
            <img
              src={eiraHeroMobileImage}
              alt=""
              className="absolute inset-0 z-[1] block h-full w-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
        </section>

        <div className="eira-hero-card project-reveal-on-scroll z-[10] flex h-[243px] w-[240px] max-w-[240px] items-center justify-center rounded-[7px] border border-solid border-[#DDDDDD] bg-white px-[30px] pb-[50px] pt-[50px] opacity-100 shadow-[0_14px_18px_-14px_rgba(0,0,0,0.45)]">
          <img
            src={eiraCardLogo}
            alt="Eira"
            width={178}
            height={141}
            className="block h-[110px] w-[104px] max-w-none object-contain opacity-100 lg:h-[141px] lg:w-[178px]"
            loading="eager"
          />
        </div>

        <img
          src={cardScrollIcon}
          alt=""
          width={20}
          height={20}
          className="eira-scroll-icon project-reveal-on-scroll project-reveal-on-scroll--delay-1 z-[10] block object-contain opacity-100"
          aria-hidden="true"
        />
      </div>

      <section
        className="eira-overview relative flex flex-col items-center bg-transparent py-[48px] max-lg:!mt-[40px] lg:h-[713px] lg:flex-row lg:bg-[#FCFBF7] lg:py-0"
        aria-label="Eira overview"
      >
        <div
          ref={overviewRevealRef}
          className="w-full max-w-full px-[24px] text-center lg:w-[48%] lg:max-w-[620px] lg:pl-[clamp(48px,7vw,120px)] lg:pr-[clamp(8px,1vw,16px)] lg:text-left"
        >
          <h2 className="eira-section-heading eira-section-heading--overview">
            {EIRA_OVERVIEW.titleLines.map((line, index) => (
              <span
                key={line}
                className={staggerLineClass}
                data-content-line
                data-reveal-delay={String(index * 120)}
              >
                {line}
              </span>
            ))}
          </h2>

          <div className="mt-[20px] lg:hidden">
            {EIRA_OVERVIEW.mobileParagraphs.map((paragraph, index) => (
              <p
                key={paragraph.slice(0, 32)}
                className={`${EIRA_OVERVIEW_BODY_LINE_CLASS} ${index > 0 ? 'mt-[16px]' : ''} ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((EIRA_OVERVIEW.titleLines.length + index) * 120)}
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-[28px] hidden lg:block">
            {EIRA_OVERVIEW.desktopBodyLines.map((line, index) => (
              <span
                key={line}
                className={`${EIRA_OVERVIEW_BODY_LINE_CLASS} ${
                  index === EIRA_OVERVIEW_SECOND_PARA_INDEX ? 'mt-[20px]' : ''
                } ${staggerLineClass}`}
                data-content-line
                data-reveal-delay={String((EIRA_OVERVIEW.titleLines.length + index) * 120)}
              >
                {line}
              </span>
            ))}
          </div>

          <button
            type="button"
            className={`mt-[32px] ${EIRA_OVERVIEW_CTA_CLASS} lg:hidden ${staggerLineClass}`}
            data-content-line
            data-reveal-delay={String((EIRA_OVERVIEW.titleLines.length + EIRA_OVERVIEW.mobileParagraphs.length) * 120)}
            onClick={handleDownloadBrochure}
          >
            Download Brochure
          </button>
          <button
            type="button"
            className={`mt-[40px] hidden ${EIRA_OVERVIEW_CTA_CLASS} lg:inline-flex ${staggerLineClass}`}
            data-content-line
            data-reveal-delay={String((EIRA_OVERVIEW.titleLines.length + EIRA_OVERVIEW.desktopBodyLines.length) * 120)}
            onClick={handleDownloadBrochure}
          >
            Download Brochure
          </button>
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--overview-art static mx-auto mt-[36px] lg:absolute lg:right-[clamp(60px,10vw,200px)] lg:top-1/2 lg:mx-0 lg:mt-0 lg:-translate-y-1/2">
          <img
            src={meetsIllustration}
            alt=""
            className="block mx-auto h-auto w-[90%] max-w-none object-contain lg:h-[clamp(333px,36.81vw,530px)] lg:w-[clamp(400px,44.17vw,636px)] lg:max-w-none"
            aria-hidden="true"
          />
        </div>
      </section>

      <section className="w-full bg-white py-[clamp(32px,6vh,80px)]" aria-label="A visual symphony">
        <h2 className="eira-section-heading eira-section-heading--visual project-reveal-on-scroll px-4">
          A Visual Symphony
        </h2>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-1 relative mt-[clamp(20px,4vh,48px)] w-full overflow-hidden">
          <div className="relative flex w-full items-center justify-center lg:hidden">
            <div className="relative z-[2] h-[483px] w-[1005px] max-w-[88%] overflow-hidden rounded-[10px]">
              <div
                className={`flex h-full w-full ${visualAnimate ? 'transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]' : ''}`}
                style={{ transform: `translateX(-${visualPos * 100}%)` }}
                onTransitionEnd={handleMobileTransitionEnd}
              >
                {EIRA_VISUAL_EXTENDED.map((pair, idx) => (
                  <div key={idx} className="relative h-full w-full shrink-0 basis-full">
                    <img
                      src={pair.m}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                      loading={idx <= 1 ? 'eager' : 'lazy'}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`eira-visual-desk-carousel mx-auto hidden w-full lg:block${
              visualAnimate ? '' : ' eira-visual-desk-carousel--no-transition'
            }`}
          >
            {EIRA_VISUAL_EXTENDED.map((pair, idx) => {
              const offset = idx - visualPos;
              const posClass = getVisualDposClass(offset);
              const isSide = Math.abs(offset) === 1;
              const handleSideClick = offset === -1
                ? () => goVisualSlide(-1)
                : offset === 1
                  ? () => goVisualSlide(1)
                  : undefined;

              return (
                <div
                  key={`eira-visual-dpos-${idx}`}
                  className={`eira-visual-dpos ${posClass}`}
                  onClick={handleSideClick}
                  onKeyDown={isSide ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSideClick?.();
                    }
                  } : undefined}
                  onTransitionEnd={offset === 0 ? handleDeskTransitionEnd : undefined}
                  role={isSide ? 'button' : undefined}
                  tabIndex={isSide ? 0 : undefined}
                  aria-label={offset === -1 ? 'Previous image' : offset === 1 ? 'Next image' : undefined}
                  aria-hidden={offset < -1 || offset > 1}
                >
                  <img
                    src={pair.d}
                    alt=""
                    aria-hidden="true"
                    className="eira-visual-dpos__img block h-full w-full object-cover object-center"
                    decoding="async"
                    loading="eager"
                  />
                </div>
              );
            })}
            {visualPos === 0 && (
              <div
                className="eira-visual-dpos eira-visual-dpos--left"
                onClick={() => goVisualSlide(-1)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goVisualSlide(-1);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Previous image"
              >
                <img
                  src={EIRA_VISUAL_PAIRS[EIRA_VISUAL_COUNT - 2].d}
                  alt=""
                  aria-hidden="true"
                  className="eira-visual-dpos__img block h-full w-full object-cover object-center"
                  decoding="async"
                  loading="eager"
                />
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => goVisualSlide(-1)}
            className="absolute left-[clamp(20px,3vw,52px)] top-1/2 z-[4] hidden -translate-y-1/2 items-center justify-center border-0 bg-transparent p-2 lg:inline-flex"
            aria-label="Previous image"
          >
            <svg
              width="17.0975"
              height="34.1944"
              viewBox="0 0 17.0975 34.1944"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M15.36 1.55 L2.15 17.1 L15.36 32.64" stroke="#FFFFFF" strokeWidth="2.08" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => goVisualSlide(1)}
            className="absolute right-[clamp(20px,3vw,52px)] top-1/2 z-[4] hidden -translate-y-1/2 items-center justify-center border-0 bg-transparent p-2 lg:inline-flex"
            aria-label="Next image"
          >
            <svg
              width="17.0975"
              height="34.1944"
              viewBox="0 0 17.0975 34.1944"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M1.74 1.55 L14.95 17.1 L1.74 32.64" stroke="#FFFFFF" strokeWidth="2.08" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </section>

      <section className="w-full bg-white py-[clamp(32px,6vh,80px)]" aria-label="Designed for every passion">
        <h2 className="eira-section-heading eira-section-heading--passion project-reveal-on-scroll px-[6px] lg:px-4">
          <span className="block lg:hidden">Designed for Every</span>
          <span className="block lg:hidden">Passion</span>
          <span className="hidden lg:inline">Designed for Every Passion</span>
        </h2>

        <div className="mx-auto mt-[clamp(24px,4vh,48px)] flex max-w-[1296px] flex-wrap items-center justify-center gap-[26px] px-[3%] lg:gap-[clamp(10px,1.4vw,24px)] lg:px-[clamp(16px,4vw,72px)]">
          {EIRA_AMENITIES.map(({ icon, label }, index) => (
            <div
              key={label}
              className="project-reveal-on-scroll flex h-[180px] w-[42%] max-w-[177.5px] flex-col items-center justify-center gap-[15px] rounded-[7px] bg-[#FCFBF7] px-[15px] py-[30px] text-center sm:w-[160px] lg:h-[200px] lg:w-[177.5px]"
              style={{ transitionDelay: `${120 + index * 90}ms` }}
            >
              <span className="eira-amenity-icon-shell" aria-hidden="true">
                <img
                  src={icon}
                  alt=""
                  className="eira-amenity-icon"
                  loading="lazy"
                />
              </span>
              <span className="font-['Poppins',sans-serif] text-[14px] font-light uppercase leading-[19px] tracking-[0] text-[#000000] lg:font-normal lg:text-[#0A0A0AC7]">
                {label}
              </span>
            </div>
          ))}
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-2 mt-[clamp(40px,8vh,96px)] flex flex-nowrap items-center justify-center gap-[5px] px-[12px] lg:mt-[clamp(56px,8vh,96px)] lg:flex-wrap lg:gap-[10px] lg:px-0">
          {EIRA_MAP_TABS.map((tab) => {
            const isActive = tab === activeMapTab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveMapTab(tab)}
                className={`inline-flex h-[30px] min-w-0 items-center justify-center whitespace-nowrap rounded-[5px] border border-solid px-[8px] font-['Poppins',sans-serif] text-[10px] font-normal transition-colors duration-200 lg:h-[38px] lg:min-w-[72px] lg:rounded-[5px] lg:px-[18px] lg:text-[15px] ${
                  isActive
                    ? 'border-transparent bg-[#BC8639] text-white'
                    : 'border-[#0C192D] bg-white text-[#0A0A0AC7] hover:border-[#BC8639]'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div className="project-reveal-on-scroll project-reveal-on-scroll--delay-3">
          <ProjectLocationMap
            project="eira"
            projectName="Eira"
            category={activeMapTab}
            center={EIRA_MAP_CENTER}
            fallbackEmbedUrl={EIRA_MAP_EMBED_URL}
            projectAreaLabel="Eira · Dubai Production City, Dubai"
            title="Eira location map"
          />
        </div>
      </section>

      <StickyNavigation wrapperClassName="home-navigation-sticky" onInquireClick={openMailModal} />

      <div className={`site-page-footer-wrap project-reveal-on-scroll relative z-[30] isolate ${viewportWidth >= 1024 ? 'bg-[#162D24]' : 'bg-white'}`}>
        <HomeContactFooter
          variant={viewportWidth >= 1024 ? 'home' : 'default'}
          sectionId="eira-contact"
        />
      </div>

      <MailingListModal
        open={mailOpen}
        closing={mailClosing}
        fullName={fullName}
        email={email}
        phone={phone}
        promotionalConsent={promotionalConsent}
        submitting={contactSubmitting}
        onClose={closeMailModal}
        onSubmit={handleMailSubmit}
        onFullNameChange={setFullName}
        onEmailChange={setEmail}
        onPhoneChange={setPhone}
        onPromotionalConsentChange={setPromotionalConsent}
      />
    </div>
  );
};

export default EiraPage;
